import { getAuth, type User } from 'firebase/auth';
import { functionHeaders } from './functionSecurity';

export interface FamilyMessageAttachment {
  id: string;
  kind: 'image' | 'video' | 'file';
  url: string;
  name: string;
  contentType: string;
  storagePath?: string;
}

export interface CreateFamilyMessageInput {
  /** One dancer in the family. The server expands the live family graph. */
  studentId: string;
  title: string;
  text: string;
  ackRequired: boolean;
  /** Already-uploaded media/files. Recipient membership stays server-derived. */
  attachments?: FamilyMessageAttachment[];
  /** Existing family thread when a staff sender posts a follow-up. */
  expectedThreadId?: string;
  /** Stable retry key. Normally generated and retained by this client module. */
  operationId?: string;
}

export interface PrepareFamilyMessageInput {
  /** One dancer in the family. The server expands the live family graph. */
  studentId: string;
  title: string;
  /**
   * Send later only. The thread still has to exist before a file can be
   * uploaded against it, but the subject line of a message nobody has sent
   * is not the family's conversation title, so the server does not write it.
   */
  forBooking?: boolean;
}

export interface PrepareFamilyMessageResult {
  threadId: string;
}

export interface CreateFamilyMessageResult {
  threadId: string;
  messageId: string;
  recipientCount: number;
  guardianCount: number;
  dancerCount: number;
  notificationCount: number;
}

export interface FamilyAttentionItem {
  threadId: string;
  messageId: string;
  title: string;
  text: string;
  authorName: string;
  createdAt: string;
  ackRequired: true;
}

export interface FamilyAttentionPage {
  items: FamilyAttentionItem[];
  hasMore: boolean;
}

export const FAMILY_ATTENTION_CHANGED_EVENT = 'ldg:family-attention-changed';
const FAMILY_ATTENTION_ENDPOINT = '/.netlify/functions/familyAttention';

interface FamilyMessageResponse extends Partial<CreateFamilyMessageResult> {
  error?: string;
}

interface FamilyMessageClientDeps {
  user?: User | null;
  fetchImpl?: typeof fetch;
  headers?: typeof functionHeaders;
}

const pendingFamilyMessageOperations = new Map<string, string>();

function newFamilyMessageOperationId(): string {
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

function familyMessageOperationKey(input: CreateFamilyMessageInput): string {
  const parts: unknown[] = [
    input.studentId,
    input.title,
    input.text,
    input.ackRequired,
    input.expectedThreadId ?? null,
  ];
  // Keep attachment-free keys byte-for-byte compatible with older tabs, but
  // never let two different media sends reuse one idempotency operation.
  if (input.attachments?.length) parts.push(input.attachments);
  return JSON.stringify(parts);
}

function familyMessageError(code: string | undefined): string {
  if (code === 'active_staff_required') return 'Your staff account is not active right now.';
  if (['family_not_found', 'family_integrity', 'active_guardian_required', 'family_recipient_required'].includes(code ?? '')) {
    return 'That family is no longer available for a group message. Refresh and try again.';
  }
  if (code === 'teacher_family_required') return 'That family is no longer connected to one of your classes. Refresh and try again.';
  if (code === 'family_changed') return 'The family links changed. Start a new Entire family message so the right people receive it.';
  if (code === 'family_too_large') return 'That family has too many linked accounts for one conversation. Ask the Director to review the family links.';
  if (code === 'too_many_requests') return 'Several family messages were started at once. Wait a moment, then try again.';
  if (code === 'server_not_configured') return 'Family messaging is not ready on this site yet.';
  if (code === 'bad_request') return 'The family message is missing something. Refresh and try again.';
  return 'The family message could not be sent. Try again.';
}

function familyAttentionError(code: string | undefined): string {
  if (code === 'active_guardian_required') return 'Your family-message access changed. Refresh and try again.';
  if (code === 'message_not_found' || code === 'attention_not_found') {
    return 'That family message no longer needs your acknowledgement.';
  }
  if (code === 'too_many_requests') return 'Family messages are updating often. Wait a moment, then try again.';
  if (code === 'attention_integrity') return 'Family acknowledgements need studio repair before they can be shown safely.';
  if (code === 'server_not_configured') return 'Family acknowledgements are not ready on this site yet.';
  return 'Family acknowledgements could not be checked. Try again.';
}

function parseFamilyAttentionItem(value: unknown): FamilyAttentionItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  if (
    typeof item.threadId !== 'string' || !item.threadId ||
    typeof item.messageId !== 'string' || !item.messageId ||
    typeof item.title !== 'string' ||
    typeof item.text !== 'string' ||
    typeof item.authorName !== 'string' ||
    typeof item.createdAt !== 'string' || !Number.isFinite(Date.parse(item.createdAt)) ||
    item.ackRequired !== true
  ) return null;
  return {
    threadId: item.threadId,
    messageId: item.messageId,
    title: item.title,
    text: item.text,
    authorName: item.authorName,
    createdAt: item.createdAt,
    ackRequired: true,
  };
}

/** Read the signed-in adult's server-authorized whole-family must-reads. */
interface FamilyAttentionClientDeps {
  user?: User | null;
  fetchImpl?: typeof fetch;
  headers?: typeof functionHeaders;
}

export async function listFamilyAttention(
  deps: FamilyAttentionClientDeps = {},
): Promise<FamilyAttentionPage> {
  const user = deps.user === undefined ? getAuth().currentUser : deps.user;
  if (!user) throw new Error('Sign in before checking family acknowledgements.');
  const response = await (deps.fetchImpl ?? fetch)(FAMILY_ATTENTION_ENDPOINT, {
    method: 'GET',
    headers: await (deps.headers ?? functionHeaders)(user, { json: false }),
    cache: 'no-store',
  });
  const payload = (await response.json().catch(() => ({}))) as {
    items?: unknown;
    hasMore?: unknown;
    error?: string;
  };
  // NOT A GUARDIAN IS AN ANSWER, NOT A FAILURE. familyAttention refuses any
  // caller who is not an active adult guardian (requireLiveGuardianUser), and
  // a staff sign-in linked to a dancer record (a Jr Staff email account with
  // users/{uid}.studentId) is exactly that caller: she can owe no family
  // acknowledgement, because only guardians are ever asked for one. Thrown,
  // this refusal left useMyUnacked failed for good on every such account,
  // which the home card, the bell and the Messages tile now show as "could
  // not check" (2026-09-30). The server has answered "none for you", so say
  // that.
  if (response.status === 403 && payload.error === 'active_guardian_required') {
    return { items: [], hasMore: false };
  }
  if (!response.ok || !Array.isArray(payload.items) || typeof payload.hasMore !== 'boolean') {
    throw new Error(familyAttentionError(payload.error));
  }
  const items = payload.items.map(parseFamilyAttentionItem);
  if (items.some((item) => item === null)) {
    throw new Error('Family acknowledgements returned an invalid response.');
  }
  return { items: items as FamilyAttentionItem[], hasMore: payload.hasMore };
}

/** Acknowledge only the caller's row and remove only their attention item. */
export async function acknowledgeFamilyAttention(
  threadId: string,
  messageId: string,
  deps: FamilyAttentionClientDeps = {},
): Promise<void> {
  const user = deps.user === undefined ? getAuth().currentUser : deps.user;
  if (!user) throw new Error('Sign in before acknowledging a family message.');
  const response = await (deps.fetchImpl ?? fetch)(FAMILY_ATTENTION_ENDPOINT, {
    method: 'POST',
    headers: await (deps.headers ?? functionHeaders)(user),
    body: JSON.stringify({ threadId, messageId }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
  };
  if (!response.ok || payload.ok !== true) {
    throw new Error(familyAttentionError(payload.error));
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(FAMILY_ATTENTION_CHANGED_EVENT));
  }
}

/**
 * Prepare the exact server-authorized family thread before uploading files.
 * The server creates no message, preview, notification, or delivery here.
 */
export async function prepareFamilyMessageThread(
  input: PrepareFamilyMessageInput,
  deps: FamilyMessageClientDeps = {},
): Promise<PrepareFamilyMessageResult> {
  const user = deps.user === undefined ? getAuth().currentUser : deps.user;
  if (!user) throw new Error('Sign in before starting a family message.');
  const response = await (deps.fetchImpl ?? fetch)('/.netlify/functions/familyMessage', {
    method: 'POST',
    headers: await (deps.headers ?? functionHeaders)(user),
    body: JSON.stringify({
      action: 'prepare',
      studentId: input.studentId,
      title: input.title,
      ...(input.forBooking === true ? { forBooking: true } : {}),
    }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    threadId?: string;
    error?: string;
  };
  if (!response.ok || typeof payload.threadId !== 'string' || !payload.threadId) {
    throw new Error(familyMessageError(payload.error));
  }
  return { threadId: payload.threadId };
}

/**
 * Start one server-authorized family conversation.
 *
 * The browser deliberately sends one student id and never recipient UIDs.
 * The function resolves the live sibling/guardian graph, enabled dancer
 * logins, and the caller's current teaching authority inside its transaction.
 */
export async function createFamilyMessage(
  input: CreateFamilyMessageInput,
  deps: FamilyMessageClientDeps = {},
): Promise<CreateFamilyMessageResult> {
  const user = deps.user === undefined ? getAuth().currentUser : deps.user;
  if (!user) throw new Error('Sign in before sending a family message.');

  // Allocate the retry identity from the caller's draft before preparation.
  // If the family changes after an uncertain send response, retrying the same
  // draft keeps the same operation id and the server returns a conflict
  // instead of delivering a duplicate to a newly derived thread.
  const operationKey = familyMessageOperationKey(input);
  const operationId = input.operationId
    ?? pendingFamilyMessageOperations.get(operationKey)
    ?? newFamilyMessageOperationId();
  if (!input.operationId) {
    pendingFamilyMessageOperations.set(operationKey, operationId);
    if (pendingFamilyMessageOperations.size > 50) {
      pendingFamilyMessageOperations.delete(pendingFamilyMessageOperations.keys().next().value ?? '');
    }
  }
  const expectedThreadId = input.expectedThreadId ?? (await prepareFamilyMessageThread(
    { studentId: input.studentId, title: input.title },
    { ...deps, user },
  )).threadId;
  const preparedInput = { ...input, expectedThreadId };

  const response = await (deps.fetchImpl ?? fetch)('/.netlify/functions/familyMessage', {
    method: 'POST',
    headers: await (deps.headers ?? functionHeaders)(user),
    body: JSON.stringify({
      action: 'send',
      studentId: preparedInput.studentId,
      title: preparedInput.title,
      text: preparedInput.text,
      ackRequired: preparedInput.ackRequired,
      ...(preparedInput.attachments?.length ? { attachments: preparedInput.attachments } : {}),
      operationId,
      expectedThreadId,
    }),
  });
  const payload = (await response.json().catch(() => ({}))) as FamilyMessageResponse;
  if (
    !response.ok ||
    typeof payload.threadId !== 'string' ||
    typeof payload.messageId !== 'string'
  ) {
    throw new Error(familyMessageError(payload.error));
  }

  if (!preparedInput.operationId
      && pendingFamilyMessageOperations.get(operationKey) === operationId) {
    pendingFamilyMessageOperations.delete(operationKey);
  }

  return {
    threadId: payload.threadId,
    messageId: payload.messageId,
    recipientCount: typeof payload.recipientCount === 'number' ? payload.recipientCount : 0,
    guardianCount: typeof payload.guardianCount === 'number' ? payload.guardianCount : 0,
    dancerCount: typeof payload.dancerCount === 'number' ? payload.dancerCount : 0,
    notificationCount: typeof payload.notificationCount === 'number' ? payload.notificationCount : 0,
  };
}
